import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import {
  saveOrganizationAddressBankInfo,
  submitOrganizationDocuments,
  updateOrganizationRegistryNumbers,
  IOrganizationDocumentUpload,
} from "../../setup/api/services/organization";
import { IOrganizationAddressBankInfoBody } from "../../setup/api/services/organization/models";
import { forceRefreshAccessToken } from "../../setup/auth/oidc";
import { useAlertStore } from "../../store/alertStore";
import styles from "./styles.module.scss";
import { BankCardIcon, CheckIcon, HomeIcon, ReviewDocIcon } from "./gateIcons";
import { ORGANIZATION_DOCUMENT_SLOTS } from "./organizationDocuments.constants";
import {
  ORGANIZATION_ADDRESS_COUNTRY_CODE,
  ORGANIZATION_BANK_CURRENCY,
} from "./organizationRegistration.constants";
import { IReviewDetails, IReviewDocument } from "./OrganizationPending";
import BankAccountModal from "./activation/BankAccountModal";
import NationalAddressModal from "./activation/NationalAddressModal";
import DocumentsModal from "./activation/DocumentsModal";
import { addressSummary, bankSummary } from "./activation/drafts";
import {
  IAddressDraft,
  IBankAccountDraft,
  IDocumentsDraft,
} from "./activation/types";

type TTask = "bank" | "address" | "documents";

interface Props {
  /** Organization the details and documents attach to. */
  organizationCode: string;
  /** Bank account already saved on the organization (returning user), shown as done. */
  savedBank?: IBankAccountDraft | null;
  /** Address already saved on the organization (returning user), shown as done. */
  savedAddress?: IAddressDraft | null;
  /** Attachments already on file (returning user); the documents task counts as done. */
  documentsOnFile?: IReviewDocument[];
  onSubmitted: (documents: IReviewDocument[], details: IReviewDetails) => void;
}

// Known backend business-rule messages for PUT /organizations/{code}/address-bank-info (see
// OrganizationServiceImpl.saveAddressAndBankInfo in authentication-service), mapped to translation
// keys instead of the generic submit error.
const KNOWN_ERROR_KEYS: Record<string, string> = {
  "organization commercialRegisterNumber or unifiedNumber is required to add bank info":
    "lbl_org_act_error_registry_required",
};

/**
 * Onboarding step between self-registration and the review-status screen: the business
 * completes three tasks — bank account, national address, required documents — each in its own
 * modal, then submits them together for review. A returning user sees whatever is already on
 * file as done, and only what they add or change is sent.
 *
 * Submission force-refreshes the access token (right after self-registration it doesn't carry
 * CLIENT_ADMIN yet, which the endpoints need), saves the CR + unified numbers (the bank IBAN is
 * validated against them, so they must be on the org first), saves the address and/or bank
 * account in one call, then uploads the documents (presigned PUT, then registered as organization attachments —
 * see submitOrganizationDocuments). Details go first because re-saving them replaces the
 * previous ones, while re-uploading documents adds duplicates; so a failed attempt is safest to
 * retry when the documents come last.
 */
const OrganizationActivation: React.FC<Props> = ({
  organizationCode,
  savedBank: savedBankProp = null,
  savedAddress: savedAddressProp = null,
  documentsOnFile = [],
  onSubmitted,
}) => {
  const { t } = useTranslation();
  // Pinned at mount: the submit's token refresh refetches my-organization, which hands back new
  // objects for the same saved values — they mustn't read as edits.
  const [savedBank] = useState(savedBankProp);
  const [savedAddress] = useState(savedAddressProp);
  const { showSuccess } = useAlertStore();
  const [openTask, setOpenTask] = useState<TTask | null>(null);
  const [bank, setBank] = useState<IBankAccountDraft | null>(savedBank);
  const [address, setAddress] = useState<IAddressDraft | null>(savedAddress);
  // Only files picked in this session — documents on file are never re-uploaded.
  const [documents, setDocuments] = useState<IDocumentsDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const closeTask = () => setOpenTask(null);
  const hasDocumentsOnFile = documentsOnFile.length > 0;

  const tasks = [
    {
      key: "bank" as const,
      icon: <BankCardIcon />,
      titleKey: "lbl_org_act_bank_title",
      done: !!bank,
      summary: bank ? bankSummary(bank) : t("lbl_org_act_bank_pending"),
    },
    {
      key: "address" as const,
      icon: <HomeIcon />,
      titleKey: "lbl_org_act_addr_title",
      done: !!address,
      summary: address
        ? addressSummary(address)
        : t("lbl_org_act_addr_pending"),
    },
    {
      key: "documents" as const,
      icon: <ReviewDocIcon />,
      titleKey: "lbl_org_act_docs_title",
      done: !!documents || hasDocumentsOnFile,
      summary:
        documents || hasDocumentsOnFile
          ? t("lbl_org_act_docs_done", {
              n: documents
                ? ORGANIZATION_DOCUMENT_SLOTS.length
                : documentsOnFile.length,
            })
          : t("lbl_org_act_docs_pending"),
    },
  ];

  const missing = tasks.filter((task) => !task.done);
  const canSubmit = !missing.length;

  const onSubmit = async () => {
    if (!canSubmit || submitting || !bank || !address) return;
    setSubmitting(true);
    setError(null);
    try {
      await forceRefreshAccessToken();

      // Modals hand back a new object on save, so identity tells what changed since load.
      const details: IOrganizationAddressBankInfoBody = {};
      if (address !== savedAddress) {
        details.address = {
          addressLabel: "WORK",
          state: address.province.name,
          city: address.city.name,
          district: address.district.name,
          street: address.street,
          buildingNo: address.buildingNo || undefined,
          postalCode: address.postalCode,
          countryCode: ORGANIZATION_ADDRESS_COUNTRY_CODE,
          nationalAddress: address.nationalAddress,
        };
      }
      if (bank !== savedBank) {
        details.bankAccount = {
          iban: bank.iban,
          currency: ORGANIZATION_BANK_CURRENCY,
          beneficiaryName: bank.beneficiaryName,
        };
        // First, and only when they changed: the bank account below is validated against them.
        if (
          bank.commercialRegisterNumber !==
            savedBank?.commercialRegisterNumber ||
          bank.unifiedNumber !== savedBank?.unifiedNumber
        ) {
          await updateOrganizationRegistryNumbers(organizationCode, {
            commercialRegisterNumber: bank.commercialRegisterNumber,
            unifiedNumber: bank.unifiedNumber,
          });
        }
      }
      if (details.address || details.bankAccount) {
        await saveOrganizationAddressBankInfo(organizationCode, details);
      }

      let reviewDocuments = documentsOnFile;
      if (documents) {
        const uploads: IOrganizationDocumentUpload[] =
          ORGANIZATION_DOCUMENT_SLOTS.map(({ type, labelKey }) => ({
            type,
            // The label doubles as the attachment title the admin reviewer sees.
            title: t(labelKey),
            file: documents[type],
          }));
        await submitOrganizationDocuments(organizationCode, uploads);
        reviewDocuments = uploads.map(({ type, title, file }) => ({
          key: type,
          title,
          fileName: file.name,
        }));
      }

      onSubmitted(reviewDocuments, {
        bank: bankSummary(bank),
        address: addressSummary(address),
      });
    } catch (err: any) {
      const errorKey = KNOWN_ERROR_KEYS[err?.error?.message];
      setError(t(errorKey ?? "lbl_org_act_submit_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.docsCard}>
      <h3>{t("lbl_org_docs_title")}</h3>
      <p>{t("lbl_org_act_subtitle")}</p>

      <div className={styles.actList}>
        {tasks.map(({ key, icon, titleKey, done, summary }) => (
          <button
            key={key}
            type="button"
            className={classNames(styles.actTask, { [styles.isDone]: done })}
            onClick={() => setOpenTask(key)}>
            <span className={styles.duIcon}>{icon}</span>
            <span className={styles.atTxt}>
              <b>{t(titleKey)}</b>
              <span className={styles.atSub} dir="auto">
                {summary}
              </span>
            </span>
            {done && (
              <span className={styles.duDone}>
                <CheckIcon />
              </span>
            )}
            <span className={styles.atCta}>
              {done ? t("lbl_org_act_edit") : t("lbl_org_act_complete")}
            </span>
          </button>
        ))}
      </div>

      {error && <p className={styles.docsError}>{error}</p>}

      <div className={styles.docsActions}>
        <button
          type="button"
          disabled={!canSubmit || submitting}
          className={classNames({ [styles.isLoading]: submitting })}
          onClick={onSubmit}>
          {submitting ? (
            <span className={styles.spinner} />
          ) : (
            t("lbl_org_act_submit")
          )}
        </button>
      </div>
      {!!missing.length && (
        <p className={styles.actMissing}>
          {t("lbl_org_act_missing", {
            tasks: missing.map((task) => t(task.titleKey)).join("، "),
          })}
        </p>
      )}

      <BankAccountModal
        visible={openTask === "bank"}
        initialValue={bank}
        onClose={closeTask}
        onSave={(value) => {
          setBank(value);
          closeTask();
          showSuccess(t("lbl_org_act_bank_saved"));
        }}
      />
      <NationalAddressModal
        visible={openTask === "address"}
        initialValue={address}
        onClose={closeTask}
        onSave={(value) => {
          setAddress(value);
          closeTask();
          showSuccess(t("lbl_org_act_addr_saved"));
        }}
      />
      <DocumentsModal
        visible={openTask === "documents"}
        initialValue={documents}
        onClose={closeTask}
        onSave={(value) => {
          setDocuments(value);
          closeTask();
          showSuccess(t("lbl_org_act_docs_saved"));
        }}
      />
    </div>
  );
};

export default OrganizationActivation;
